import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Sparkles, ChevronRight, ChevronLeft, Check, Upload, ArrowLeft, Loader2 } from 'lucide-react'
import PageHeader from '@/components/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import api from '@/lib/api'
import { modulesAPI, settingsAPI, tournamentsAPI } from '@/lib/api'

/* deriveInitials fallback */
function initials(name) {
  if (!name) return 'LN'
  const words = name.replace(/[&/]/g, ' ').split(/\s+/).filter(w => w && /[A-Za-z]/.test(w[0]))
  if (words.length === 0) return name.slice(0, 2).toUpperCase()
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}

const STEPS = ['Identiteit', 'Branding', 'Modules', 'Snelstart', 'Bevestig']

export default function LanWizardPage() {
  const nav = useNavigate()
  const [step, setStep] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const [edition, setEdition] = useState({ name: '', description: '', starts_at: '', ends_at: '' })
  const [branding, setBranding] = useState({ site_name: '', site_subtitle: '', footer_text: '', brand_mark: '', brand_logo_url: '' })
  const [modules, setModules] = useState({})
  const [allModules, setAllModules] = useState([])
  const [templates, setTemplates] = useState([])
  const [seedTournament, setSeedTournament] = useState(null)   // { template_id, name } | null
  const [seedVisionShow, setSeedVisionShow] = useState(null)    // { name, edition } | null

  // Prefill branding sitenaam = edition.name als user er niks van maakt
  useEffect(() => {
    if (edition.name && !branding.site_name) {
      setBranding(b => ({ ...b, site_name: edition.name }))
    }
  }, [edition.name])

  // Fetch modules + templates
  useEffect(() => {
    api.get('/modules').then(r => {
      const list = r.data.modules || r.data || []
      setAllModules(list)
      const init = {}
      for (const m of list) init[m.module_key] = m.is_enabled
      setModules(init)
    }).catch(() => {})
    tournamentsAPI.listTemplates().then(r => setTemplates(r.data.templates || [])).catch(() => {})
  }, [])

  const canNext = () => {
    if (step === 0) return edition.name.trim().length > 0
    return true
  }

  const apply = async () => {
    setBusy(true); setError('')
    try {
      const body = {
        edition, branding, modules,
        seeds: { tournament: seedTournament, vision_show: seedVisionShow },
      }
      const r = await api.post('/lan-editions/wizard-apply', body)
      try { sessionStorage.setItem('brandingCache', JSON.stringify(branding)) } catch {}
      nav('/')
      setTimeout(() => window.location.reload(), 100)
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    } finally { setBusy(false) }
  }

  const uploadLogo = async (file) => {
    if (!file) return
    setBusy(true); setError('')
    try {
      const r = await settingsAPI.uploadBrandingLogo(file)
      if (r.data?.brand_logo_url) setBranding(b => ({ ...b, brand_logo_url: r.data.brand_logo_url }))
    } catch (e) { setError('Upload: ' + (e.response?.data?.error || e.message)) }
    finally { setBusy(false) }
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <PageHeader
        icon={Sparkles}
        eyebrow="Beheer · nieuwe LAN"
        title="LAN wizard"
        subtitle="Zet een nieuwe LAN party op — identiteit, branding, modules en optioneel een startshow of toernooi."
        actions={<Button asChild variant="outline"><Link to="/admin/lan-edities"><ArrowLeft className="h-4 w-4 mr-2" />Terug</Link></Button>}
      />

      {/* Stepper */}
      <div className="grid grid-cols-5 gap-2">
        {STEPS.map((label, i) => (
          <div key={i} className={`text-center py-2 rounded-md border ${
            i === step ? 'border-acid bg-acid/10 text-acid' :
            i < step ? 'border-border bg-card text-muted-foreground' :
                        'border-border bg-card text-muted-foreground opacity-50'
          }`}>
            <div className="font-mono text-[10px] uppercase tracking-widest">Stap {i + 1}</div>
            <div className="text-sm font-medium">{label}</div>
          </div>
        ))}
      </div>

      {error && <div className="rounded-md border border-signal bg-signal/10 text-signal p-3 text-sm">⚠ {error}</div>}

      <Card>
        <CardContent className="p-5">
          {/* STEP 0: identiteit */}
          {step === 0 && (
            <div className="space-y-3">
              <Field label="Naam van de LAN" required>
                <input value={edition.name} onChange={e => setEdition({ ...edition, name: e.target.value })}
                       className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm"
                       placeholder="Bijv. Bear & Bockie LAN · Editie 13" />
              </Field>
              <Field label="Beschrijving">
                <input value={edition.description || ''} onChange={e => setEdition({ ...edition, description: e.target.value })}
                       className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Startdatum">
                  <input type="date" value={edition.starts_at || ''} onChange={e => setEdition({ ...edition, starts_at: e.target.value })}
                         className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm" />
                </Field>
                <Field label="Einddatum">
                  <input type="date" value={edition.ends_at || ''} onChange={e => setEdition({ ...edition, ends_at: e.target.value })}
                         className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm" />
                </Field>
              </div>
            </div>
          )}

          {/* STEP 1: branding */}
          {step === 1 && (
            <div className="space-y-3">
              <Field label="Sitenaam">
                <input value={branding.site_name} onChange={e => setBranding({ ...branding, site_name: e.target.value })}
                       maxLength={60}
                       className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm" />
              </Field>
              <Field label="Subtitel">
                <input value={branding.site_subtitle} onChange={e => setBranding({ ...branding, site_subtitle: e.target.value })}
                       maxLength={60}
                       className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm" />
              </Field>
              <Field label="Footer tekst">
                <input value={branding.footer_text} onChange={e => setBranding({ ...branding, footer_text: e.target.value })}
                       maxLength={200}
                       className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm" />
              </Field>
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border/40">
                <Field label={`Afkorting (auto: ${initials(branding.site_name || 'LN')})`}>
                  <input value={branding.brand_mark} onChange={e => setBranding({ ...branding, brand_mark: e.target.value.slice(0, 4) })}
                         maxLength={4}
                         className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm font-bold text-acid"
                         placeholder="KL" />
                </Field>
                <Field label="Of logo (max 2MB)">
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={e => uploadLogo(e.target.files?.[0])} />
                    <div className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm flex items-center gap-2 hover:border-acid">
                      <Upload className="h-4 w-4" />
                      {branding.brand_logo_url ? 'Vervang' : 'Kies bestand'}
                    </div>
                  </label>
                  {branding.brand_logo_url && (
                    <div className="mt-2 flex items-center gap-2">
                      <img src={branding.brand_logo_url} className="h-8 w-8 object-contain rounded" />
                      <button onClick={() => setBranding({ ...branding, brand_logo_url: '' })} type="button"
                              className="text-xs text-signal">Verwijder</button>
                    </div>
                  )}
                </Field>
              </div>
            </div>
          )}

          {/* STEP 2: modules */}
          {step === 2 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Vink aan welke modules zichtbaar zijn tijdens deze LAN. Admins zien alles ongeacht.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {allModules.map(m => (
                  <label key={m.module_key} className="flex items-center gap-2 p-2 rounded border border-border hover:border-acid/50 cursor-pointer">
                    <input type="checkbox" checked={!!modules[m.module_key]}
                           onChange={e => setModules({ ...modules, [m.module_key]: e.target.checked })} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{m.module_name || m.module_key}</div>
                      <div className="font-mono text-[10px] text-muted-foreground truncate">{m.module_key}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* STEP 3: seeds */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1" checked={!!seedTournament}
                         onChange={e => setSeedTournament(e.target.checked ? { template_id: templates[0]?.id, name: templates[0]?.name || '' } : null)} />
                  <div className="flex-1">
                    <div className="font-medium">Meteen een toernooi klaarzetten</div>
                    <div className="text-xs text-muted-foreground">Kies een template — het toernooi wordt aan deze LAN gekoppeld.</div>
                  </div>
                </label>
                {seedTournament && templates.length > 0 && (
                  <div className="mt-2 space-y-2 pl-6">
                    <select value={seedTournament.template_id || ''}
                            onChange={e => {
                              const tpl = templates.find(t => t.id === Number(e.target.value))
                              setSeedTournament({ template_id: tpl?.id, name: tpl?.name || '' })
                            }}
                            className="w-full h-9 rounded-[4px] border border-border bg-background px-3 text-sm">
                      {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    <input value={seedTournament.name}
                           onChange={e => setSeedTournament({ ...seedTournament, name: e.target.value })}
                           className="w-full h-9 rounded-[4px] border border-border bg-background px-3 text-sm"
                           placeholder="Toernooi naam (default = template naam)" />
                  </div>
                )}
              </div>

              <div className="border-t border-border/40 pt-4">
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1" checked={!!seedVisionShow}
                         onChange={e => setSeedVisionShow(e.target.checked ? { name: '', edition: '' } : null)} />
                  <div className="flex-1">
                    <div className="font-medium">Meteen een Kempenvision show klaarzetten</div>
                    <div className="text-xs text-muted-foreground">Show wordt aangemaakt in "setup" fase, inschrijvingen open.</div>
                  </div>
                </label>
                {seedVisionShow && (
                  <div className="mt-2 space-y-2 pl-6">
                    <input value={seedVisionShow.name}
                           onChange={e => setSeedVisionShow({ ...seedVisionShow, name: e.target.value })}
                           className="w-full h-9 rounded-[4px] border border-border bg-background px-3 text-sm"
                           placeholder="Show naam (bijv. Kempenvision · Winter 26)" />
                    <input value={seedVisionShow.edition}
                           onChange={e => setSeedVisionShow({ ...seedVisionShow, edition: e.target.value })}
                           className="w-full h-9 rounded-[4px] border border-border bg-background px-3 text-sm"
                           placeholder="Editie (optioneel)" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 4: bevestigen */}
          {step === 4 && (
            <div className="space-y-3 text-sm">
              <div className="text-muted-foreground mb-3">
                Bij "Start deze LAN": nieuwe editie wordt actief, huidige wordt gearchiveerd, branding + modules worden toegepast, optionele seeds worden aangemaakt. <b>Alles atomisch</b> — bij een fout blijft alles zoals het was.
              </div>
              <Row k="LAN" v={edition.name} />
              {edition.starts_at && <Row k="Start" v={edition.starts_at} />}
              {edition.ends_at   && <Row k="Einde" v={edition.ends_at} />}
              <Row k="Sitenaam" v={branding.site_name} />
              {branding.brand_logo_url
                ? <Row k="Merkteken" v={<img src={branding.brand_logo_url} className="h-6 w-6 inline object-contain" />} />
                : <Row k="Merkteken" v={<span className="font-bold text-acid">{branding.brand_mark || initials(branding.site_name)}</span>} />}
              <Row k="Modules aan"  v={Object.values(modules).filter(Boolean).length} />
              <Row k="Modules uit"  v={Object.values(modules).filter(v => !v).length} />
              {seedTournament && <Row k="Seed toernooi" v={seedTournament.name} />}
              {seedVisionShow && <Row k="Seed KV show"  v={seedVisionShow.name} />}
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between items-center pt-5 mt-5 border-t border-border/40">
            <Button variant="outline" disabled={step === 0 || busy}
                    onClick={() => setStep(s => Math.max(0, s - 1))}>
              <ChevronLeft className="h-4 w-4 mr-1" />Vorige
            </Button>
            {step < STEPS.length - 1 ? (
              <Button disabled={!canNext() || busy}
                      onClick={() => setStep(s => s + 1)}>
                Volgende<ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button disabled={busy || !edition.name} onClick={apply}>
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                Start deze LAN
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function Field({ label, required, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}{required && ' *'}
      </span>
      {children}
    </label>
  )
}
function Row({ k, v }) {
  return (
    <div className="grid grid-cols-[10rem_1fr] gap-3 py-1">
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground pt-1">{k}</div>
      <div>{v}</div>
    </div>
  )
}
